import { useEffect, useState } from 'react';
import { ShieldCheck, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { emptyPin, PinInput, pinArrayToString } from '@/components/user/PinInput';
import { notify, userAccountService } from '@/services';
import type { PublicUser } from '@/types/auth';

interface ChangePinModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (user: PublicUser) => void;
}

export function ChangePinModal({ open, onClose, onSuccess }: ChangePinModalProps) {
  const [currentPin, setCurrentPin] = useState(emptyPin());
  const [newPin, setNewPin] = useState(emptyPin());
  const [loading, setLoading] = useState(false);

  const currentComplete = currentPin.every(Boolean);
  const newComplete = newPin.every(Boolean);
  const canSubmit = currentComplete && newComplete && !loading;

  useEffect(() => {
    if (open) {
      setCurrentPin(emptyPin());
      setNewPin(emptyPin());
    }
  }, [open]);

  async function handleSubmit() {
    const current = pinArrayToString(currentPin);
    const next = pinArrayToString(newPin);

    if (!/^\d{4}$/.test(current)) {
      notify.error('Enter your current 4-digit PIN');
      return;
    }

    if (!/^\d{4}$/.test(next)) {
      notify.error('New PIN must be exactly 4 digits');
      return;
    }

    if (current === next) {
      notify.error('New PIN must be different from your current PIN');
      setNewPin(emptyPin());
      return;
    }

    setLoading(true);
    try {
      const res = await userAccountService.changeTransactionPin(current, next, next);
      if (!res.success || !res.data?.user) throw new Error(res.message || 'Failed to change PIN');
      onSuccess(res.data.user);
      notify.apiSuccess(res.message, 'Transaction PIN changed successfully');
      onClose();
    } catch (err) {
      notify.apiError(err, 'Failed to change PIN');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xs"
      hideClose
      bodyClassName="py-4"
      footer={
        <div className="flex w-full gap-2">
          <Button variant="outline" size="sm" className="flex-1 sm:flex-none" disabled={loading} onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" className="flex-1" loading={loading} loadingText="Saving…" disabled={!canSubmit} onClick={() => void handleSubmit()}>
            Change PIN
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-brand text-white">
            <ShieldCheck className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1 pt-0.5">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-base font-bold leading-tight text-brand-navy">Change transaction PIN</h3>
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="-mr-1 rounded-md p-1 text-brand-muted transition hover:bg-brand-surface hover:text-brand-navy disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-0.5 text-xs text-brand-muted">Enter your current PIN and choose a new one</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-center text-sm font-medium text-brand-navy">Current PIN</p>
            <div className="rounded-xl bg-brand-surface/70 px-3 py-4">
              <PinInput value={currentPin} onChange={setCurrentPin} autoFocus disabled={loading} />
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-center text-sm font-medium text-brand-navy">New PIN</p>
            <div className="rounded-xl bg-brand-surface/70 px-3 py-4">
              <PinInput value={newPin} onChange={setNewPin} disabled={loading} />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
